// One voice session owns its microphone, timer, and upload. Cancel invalidates
// pending permission/upload results as well as stopping active recording.
export function createAgentVoice({ onListening, onBusy, onText, onError, transcribe, env = window }) {
  let session = null;
  const release = current => {
    env.clearTimeout(current.timer);
    current.stream?.getTracks().forEach(track => track.stop());
  };
  const cancel = () => {
    const current = session;
    session = null;
    if (!current) return;
    current.controller?.abort();
    current.recognition?.abort();
    if (current.recorder?.state === "recording") current.recorder.stop();
    release(current);
    onListening(false); onBusy(false);
  };
  const fail = (current, message) => {
    if (session !== current) return;
    cancel(); onError(message);
  };
  const finish = (current, text) => {
    if (session !== current) return;
    session = null; release(current); onListening(false); onBusy(false);
    if (text?.trim()) onText(text.trim());
    else onError("No speech was detected. Please try again.");
  };
  const stop = () => {
    if (session?.recorder?.state === "recording") session.recorder.stop();
    else session?.recognition?.stop();
  };
  const start = async () => {
    if (session) return;
    if (env.isSecureContext === false) {
      onError("Microphone access requires HTTPS. Open the secure site to use voice input."); return;
    }
    const current = { controller: new AbortController() };
    session = current; onError("");
    // Record on mobile, where native recognition can exist but fail to start.
    const mobile = /Android|iPhone|iPad|iPod/i.test(env.navigator.userAgent) || (env.navigator.maxTouchPoints > 1 && /Macintosh/.test(env.navigator.userAgent));
    const Recognition = env.SpeechRecognition || env.webkitSpeechRecognition;
    const canRecord = env.navigator.mediaDevices?.getUserMedia && env.MediaRecorder;
    if (canRecord && (mobile || !Recognition)) {
      onBusy(true);
      try {
        current.stream = await env.navigator.mediaDevices.getUserMedia({ audio: true });
        if (session !== current) { release(current); return; }
        const mimeType = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm", "audio/ogg;codecs=opus"].find(type => env.MediaRecorder.isTypeSupported(type));
        if (!mimeType) throw new Error("Unsupported recording format");
        const recorder = new env.MediaRecorder(current.stream, { mimeType });
        current.recorder = recorder;
        const chunks = [];
        let bytes = 0;
        recorder.ondataavailable = event => {
          if (event.data.size) { chunks.push(event.data); bytes += event.data.size; }
          if (bytes > 14 * 1024 * 1024) fail(current, "Recording is too long. Please record a shorter message.");
        };
        recorder.onerror = () => fail(current, "Microphone recording failed. Please try again.");
        recorder.onstop = async () => {
          release(current);
          if (session !== current) return;
          onListening(false); onBusy(true);
          try {
            const text = await transcribe(new Blob(chunks, { type: mimeType }), current.controller.signal);
            finish(current, text);
          } catch (error) { fail(current, error.message || "Unable to transcribe recording."); }
        };
        recorder.start(1000); onBusy(false); onListening(true);
        current.timer = env.setTimeout(stop, 120000);
      } catch (error) {
        fail(current, error.name === "NotAllowedError" ? "Allow microphone access in your browser settings, then try again." : "Unable to access the microphone. Check permissions and close other apps using it.");
      }
      return;
    }
    if (!Recognition) { fail(current, "Voice input is unavailable in this browser. Please use a browser with microphone support or type your message."); return; }
    try {
      const recognition = new Recognition(); current.recognition = recognition;
      recognition.continuous = false; recognition.interimResults = true; recognition.lang = "en-US";
      let text = "";
      recognition.onresult = event => {
        text = Array.from(event.results).map(result => result[0]?.transcript || "").join(" ");
      };
      recognition.onerror = event => fail(current, event.error === "not-allowed" ? "Allow microphone access in your browser settings, then try again." : `Voice input failed (${event.error || "unknown error"}). Please try again.`);
      recognition.onend = () => finish(current, text);
      recognition.start(); onListening(true);
      current.timer = env.setTimeout(stop, 120000);
    } catch { fail(current, "Unable to start the microphone. Please try again."); }
  };
  return { start, stop, cancel };
}
